/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";

// Soft UI Dashboard PRO React helper functions
import pxToRem from "assets/theme/functions/pxToRem";
import boxShadow from "assets/theme/functions/boxShadow";

// @emotion/react components
import { keyframes } from "@emotion/react";

export default (selectSize, selectError, selectSuccess, compact = false, compactMenu = false) => {
  const { dark, white, text, light, inputColors, gradients, transparent } = colors;
  const { size, fontFamily, fontWeightRegular } = typography;
  const listingTextColor = "#334155";
  const { borderWidth, borderRadius } = borders;
  const { inputBoxShadow, lg } = boxShadows;

  let borderColorValue;
  let borderColorFocused;
  let boxShadowValue;
  let selectSizeValue;

  if (selectError) {
    borderColorValue = inputColors.error;
    borderColorFocused = inputColors.error;
  } else if (selectSuccess) {
    borderColorValue = inputColors.success;
    borderColorFocused = inputColors.success;
  } else {
    borderColorValue = inputColors.borderColor.main;
    borderColorFocused = inputColors.borderColor.focus;
  }

  if (selectError) {
    boxShadowValue = inputBoxShadow.error;
  } else if (selectSuccess) {
    boxShadowValue = inputBoxShadow.success;
  } else {
    boxShadowValue = boxShadow([0, 0], [0, 2], inputColors.boxShadow, 1);
  }

  if (selectSize === "small") {
    selectSizeValue = pxToRem(36);
  } else if (selectSize === "large") {
    selectSizeValue = pxToRem(48);
  } else {
    selectSizeValue = pxToRem(40);
  }

  const cssVar = (name, fallback) => `var(${name}, ${fallback})`;
  const selectHeightValue = compact ? "28px" : cssVar("--soft-select-height", selectSizeValue);
  const selectFontSizeValue = compact ? "10px" : cssVar("--soft-select-font-size", size.sm);
  const selectValueHeight = compact ? "26px" : cssVar("--soft-select-value-height", "max-content");
  const selectValuePx = compact ? "8px" : cssVar("--soft-select-value-px", pxToRem(12));
  const selectTextTop = compact ? "50%" : cssVar("--soft-select-text-top", selectSize === "small" ? "52%" : "51%");
  const selectIndicatorPadding = compact ? "4px" : cssVar("--soft-select-indicator-padding", pxToRem(12));
  const selectIndicatorSize = compact ? "12px" : cssVar("--soft-select-indicator-size", pxToRem(14));
  const selectMenuPadding = compact ? "4px" : cssVar("--soft-select-menu-padding", `${pxToRem(16)} ${pxToRem(8)}`);
  const selectOptionPadding = compact ? "4px 8px" : cssVar("--soft-select-option-padding", `${pxToRem(7)} ${pxToRem(12)}`);
  const selectBorderRadius = compact ? "6px" : cssVar("--soft-select-border-radius", borderRadius.md);
  const selectTextColor = compact ? listingTextColor : text.main;
  const selectLineHeight = compact ? 1.15 : undefined;
  const selectMenuMinWidth = compact ? "100%" : pxToRem(160);
  const menuFontSizeValue = compact || compactMenu ? "10px" : selectFontSizeValue;
  const menuPaddingValue = compact || compactMenu ? "4px" : selectMenuPadding;
  const optionPaddingValue = compact || compactMenu ? "4px 8px" : selectOptionPadding;
  const menuTextColor = compact || compactMenu ? listingTextColor : selectTextColor;
  const menuLineHeight = compact || compactMenu ? 1.15 : selectLineHeight;
  const menuMinWidth = compact || compactMenu ? "100%" : selectMenuMinWidth;

  // animations
  const prespective = keyframes`
    from {
      opacity: 0;
      transform: perspective(999px) rotateX(-10deg) translateZ(0) translate3d(0, 0, 0);
    }
    to {
      opacity: 1;
      transform: perspective(999px) rotateX(0deg) translateZ(0) translate3d(0, 0, 5px);
    }
  `;

  return {
    control: (provided, state) => ({
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: selectHeightValue,
      padding: 0,
      minHeight: selectHeightValue,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      ...(selectLineHeight ? { lineHeight: selectLineHeight } : {}),
      backgroundColor: transparent.main,
      backgroundClip: "padding-box",
      border: `${borderWidth[1]} solid ${inputColors.borderColor.main}`,
      appearance: "none",
      borderRadius: selectBorderRadius,
      transition: "box-shadow 150ms ease, border-color 150ms ease",
      cursor: "pointer",
      borderColor: state.isFocused ? borderColorFocused : borderColorValue,
      boxShadow: state.isFocused ? boxShadowValue : "none",
    }),

    valueContainer: () => ({
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: selectValueHeight,
      padding: `0 ${selectValuePx}`,
      color: selectTextColor,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      ...(selectLineHeight ? { lineHeight: selectLineHeight } : {}),
    }),

    placeholder: () => ({
      position: "absolute",
      top: selectTextTop,
      transform: "translateY(-50%)",
      marginLeft: 0,
      marginRight: 0,
      color: dark.main,
      opacity: 0.5,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      ...(selectLineHeight ? { lineHeight: selectLineHeight } : {}),
    }),

    singleValue: () => ({
      position: "absolute",
      top: selectTextTop,
      transform: "translateY(-50%)",
      color: selectTextColor,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      ...(selectLineHeight ? { lineHeight: selectLineHeight } : {}),
    }),

    input: () => ({
      color: selectTextColor,
      position: "relative",
      margin: 0,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      ...(selectLineHeight ? { lineHeight: selectLineHeight } : {}),
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    dropdownIndicator: (provided, state) => ({
      display: "flex",
      padding: selectIndicatorPadding,

      "& svg": {
        fill: text.main,
        width: selectIndicatorSize,
        height: selectIndicatorSize,
        strokeWidth: 1,
        stroke: text.main,
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0)",
        transition: "transform 250ms ease",
      },
    }),

    menu: (provided) => ({
      ...provided,
      minWidth: menuMinWidth,
      boxShadow: lg,
      padding: menuPaddingValue,
      fontSize: menuFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      color: menuTextColor,
      ...(menuLineHeight ? { lineHeight: menuLineHeight } : {}),
      textAlign: "left",
      backgroundColor: white.main,
      borderRadius: borderRadius.md,
      overflowX: "hidden",
      transformOrigin: "50% 0",
      animation: `${prespective} 250ms ease forwards !important`,
      zIndex: 9999, // ✅ HIGHEST Z-INDEX ADDED
      position: "relative", // ✅ ENSURE PROPER POSITIONING
    }),

    menuList: (provided) => ({
      ...provided,
      padding: 0,
      overflowX: "hidden",
    }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999, // ✅ FOR PORTAL VERSION
    }),

    option: (provided, state) => ({
      ...provided,
      position: "relative",
      minWidth: menuMinWidth,
      minHeight: "unset",
      padding: optionPaddingValue,
      borderRadius: selectBorderRadius,
      fontSize: menuFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      color: menuTextColor,
      ...(menuLineHeight ? { lineHeight: menuLineHeight } : {}),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.5 : 1,
      userSelect: state.isDisabled ? "none" : "auto",
      transition: "background-color 300ms ease, color 300ms ease",

      "&:hover, &:focus": {
        backgroundColor: state.isDisabled ? transparent.main : light.main,
        color: state.isDisabled ? "currentColor" : dark.main,
      },
    }),

    multiValue: (provided) => ({
      ...provided,
      margin: 0,
      marginRight: pxToRem(4),
      borderRadius: borderRadius.section,
      display: "flex",
      alignItems: "center",
      backgroundColor: gradients.dark.state,
      color: white.main,
      fontSize: selectFontSizeValue,
      fontFamily,
      fontWeight: fontWeightRegular,
      padding: `${pxToRem(2)} 0 ${pxToRem(2)} ${pxToRem(4)}`,

      "& div:first-of-type": {
        color: white.main,
        fontSize: selectFontSizeValue,
        fontFamily,
        fontWeight: fontWeightRegular,
        paddingTop: pxToRem(4),
        paddingBottom: pxToRem(2),
      },

      "& div:last-of-type": {
        paddingTop: pxToRem(1.5),
        opacity: 0.8,

        "&:hover": {
          backgroundColor: transparent.main,
          color: white.main,
        },
      },
    }),
  };
};
